import { Controller } from "@hotwired/stimulus";
import "./component.scss";

const SYNC_EVENT = "affiliate-tiers:mode";

// Affiliate commission tier field. Renders all five tier inputs always; this controller
// flips the presentational mode (single ⇄ multi) via a data-mode attribute on the root.
// "mehrstufiges Programm" is program-wide, so toggling one instance broadcasts to the rest.
export default class extends Controller {
  static values = {
    multiLevel: Boolean,
    readOnly: Boolean,
  };

  connect() {
    // multiLevelValueChanged fires on initialization, so #applyMode has already run here.
    this.onSync = this.#onSync.bind(this);

    if (!this.readOnlyValue) {
      document.addEventListener(SYNC_EVENT, this.onSync);
    }
  }

  disconnect() {
    document.removeEventListener(SYNC_EVENT, this.onSync);
  }

  // Public action — wire to a checkbox's change event, e.g.
  // data-action="change->form-affiliate-tiers#toggle"
  // Reads the checkbox's actual checked state instead of flipping an internal flag, so it
  // stays correct even on an instance whose multiLevel value was never seeded (the bare
  // wrapper that hosts this action), then broadcasts the new mode to the sibling instances.
  toggle(event) {
    const multiLevel = event.target.checked;
    this.setMode(multiLevel);
    document.dispatchEvent(
      new CustomEvent(SYNC_EVENT, {
        detail: { multiLevel },
      }),
    );
  }

  // Programmatic setter for driving the mode from another controller without the event.
  setMode(multiLevel) {
    this.multiLevelValue = multiLevel; // triggers multiLevelValueChanged
  }

  // Holds a tier input to the 0..100 range as the user types — `max=` alone does not block
  // typing values like 250. Wired onto every tier input via the component's field_options.
  clamp(event) {
    const input = event.target;
    if (input.value === "") return;

    const value = Number(input.value);
    if (Number.isNaN(value)) return;
    if (value > 100) input.value = "100";
    else if (value < 0) input.value = "0";
  }

  multiLevelValueChanged() {
    this.#applyMode();
  }

  #applyMode() {
    this.element.dataset.mode = this.multiLevelValue ? "multi" : "single";
    this.element.dataset.fullWidth = this.multiLevelValue ? "full" : "compact";
  }

  // A sibling toggled — match it, but don't re-broadcast (no feedback loop).
  #onSync(event) {
    this.setMode(event.detail.multiLevel);
  }
}
